import { useState, useEffect } from 'react';

interface HeaderProps {
  isAuthenticated: boolean;
  userName?: string;
  credits?: number;
  onOpenLogin: () => void;
  onLogout: () => void;
}

export default function Header({ isAuthenticated, userName = '', credits = 0, onOpenLogin, onLogout }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const scrollToSection = (sectionId: string) => {
    // If not on landing page, save target section and redirect to home
    if (window.location.pathname !== '/') {
      localStorage.setItem('pendingScrollSection', sectionId);
      window.location.href = '/';
      return;
    }

    // Execute scroll logic on the landing page
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const offsetTop = element.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetTop < 0 ? 0 : offsetTop, behavior: 'smooth' });
      setMobileMenuOpen(false);
      return;
    }
  };

  // On mount, if a pending scroll target exists (from another route), perform it
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pending = localStorage.getItem('pendingScrollSection');
      if (pending && window.location.pathname === '/') {
        localStorage.removeItem('pendingScrollSection');
        setTimeout(() => scrollToSection(pending), 100);
      }
    }
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-[rgba(15,23,42,0.96)] border-b border-[#1f2937] backdrop-blur-[10px]">
      <div className="max-w-[1120px] mx-auto px-4 flex items-center justify-between py-2.5 gap-3">
        <button onClick={() => scrollToSection('topo')} className="flex items-center bg-transparent border-none p-0">
          <div className="w-10 h-10 rounded-full bg-[conic-gradient(from_220deg,#e2b714,#e86fde,#4da9ff,#e2b714)] p-[2px]">
            <div className="w-full h-full rounded-full bg-[#05060a] flex items-center justify-center text-lg font-bold tracking-wide">
              TV
            </div>
          </div>
        </button>

        <nav className={`flex gap-4 text-[0.95rem] ${mobileMenuOpen ? 'nav-open' : ''} max-[900px]:hidden`}>
          <button onClick={() => scrollToSection('consultores')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] transition-colors">
            Consultores
          </button>
          <button onClick={() => scrollToSection('como-funciona')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] transition-colors">
            Como funciona
          </button>
          <button onClick={() => scrollToSection('planos')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] transition-colors">
            Comprar minutos
          </button>
          <button onClick={() => scrollToSection('faq')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] transition-colors">
            FAQ
          </button>
          <button onClick={() => scrollToSection('contacto')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] transition-colors">
            Contacto
          </button>
        </nav>


        <div className={`flex gap-2 items-center ${mobileMenuOpen ? 'nav-open' : ''} max-[900px]:hidden`}>
          {isAuthenticated && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(226,183,20,0.15)] border border-[rgba(226,183,20,0.4)]">
              <span className="text-[0.85rem] text-[#9ca3af]">Créditos:</span>
              <span className="text-[0.95rem] font-semibold text-[#facc15]">{credits.toFixed(2)} €</span>
            </div>
          )}
          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[rgba(148,163,184,0.9)] px-4 py-2 text-[0.95rem] font-medium cursor-pointer bg-transparent text-[#e5e7eb] hover:bg-[rgba(15,23,42,0.9)] hover:border-[#f97316] transition-colors"
              >
                <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] flex items-center justify-center text-xs font-bold">
                  {userName.charAt(0).toUpperCase()}
                </span>
                <span>{userName.slice(0, 10)}</span>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 bg-[#020617] rounded-xl border border-[#111827] p-2 min-w-[180px] shadow-xl">
                  <a 
                    href="/user-dashboard" 
                    className="block px-3 py-2 rounded-lg text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] transition-colors"
                  >
                    Área do cliente
                  </a>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      onLogout();
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] transition-colors bg-transparent border-none cursor-pointer"
                  >
                    Sair
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={onOpenLogin}
              className="inline-flex items-center justify-center rounded-full border border-[rgba(148,163,184,0.9)] px-4 py-2 text-[0.95rem] font-medium cursor-pointer bg-transparent text-[#e5e7eb] hover:bg-[rgba(15,23,42,0.9)] hover:border-[#f97316] transition-colors"
            >
              Entrar
            </button>
          )}
          <button
            onClick={() => scrollToSection('planos')}
            className="inline-flex items-center justify-center rounded-full border-none px-4 py-2 text-[0.95rem] font-medium cursor-pointer bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] text-[#f9fafb] shadow-[0_8px_20px_rgba(15,23,42,0.6)] hover:translate-y-[-1px] hover:brightness-105 hover:shadow-[0_12px_25px_rgba(15,23,42,0.75)] transition-all"
          >
            Comprar minutos
          </button>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="hidden max-[900px]:block bg-transparent border-none text-2xl cursor-pointer text-[#e5e7eb]"
          aria-label="Menu"
        >
          ☰
        </button>

        {mobileMenuOpen && (
          <div className="hidden max-[900px]:flex flex-col absolute top-14 right-4 bg-[#020617] rounded-xl border border-[#111827] p-3 gap-2 min-w-[200px] shadow-lg">
            <button onClick={() => scrollToSection('consultores')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] text-left">
              Consultores
            </button>
            <button onClick={() => scrollToSection('como-funciona')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] text-left">
              Como funciona
            </button>
            <button onClick={() => scrollToSection('planos')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] text-left">
              Comprar minutos
            </button>
            <button onClick={() => scrollToSection('faq')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] text-left">
              FAQ
            </button>
            <button onClick={() => scrollToSection('contacto')} className="px-2 py-1.5 rounded-full text-[#e5e7eb] hover:bg-[rgba(248,250,252,0.1)] text-left">
              Contacto
            </button>
            {isAuthenticated && (
              <div className="flex items-center justify-between px-3 py-2 rounded-full bg-[rgba(226,183,20,0.15)] border border-[rgba(226,183,20,0.4)] mx-1">
                <span className="text-[0.8rem] text-[#9ca3af]">Créditos:</span>
                <span className="text-[0.9rem] font-semibold text-[#facc15]">{credits} min</span>
              </div>
            )}
            {isAuthenticated ? (
              <>
                <a href="/user-dashboard" className="px-4 py-2 border border-[rgba(148,163,184,0.9)] rounded-full text-center text-[#e5e7eb]">
                  Área do cliente
                </a>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogout();
                  }}
                  className="px-4 py-2 border border-[rgba(148,163,184,0.9)] rounded-full text-center text-[#e5e7eb] bg-transparent cursor-pointer"
                >
                  Sair
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenLogin();
                }}
                className="px-4 py-2 border border-[rgba(148,163,184,0.9)] rounded-full text-center text-[#e5e7eb] bg-transparent cursor-pointer"
              >
                Entrar
              </button>
            )}
            <button onClick={() => scrollToSection('planos')} className="px-4 py-2 bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] rounded-full text-center text-[#f9fafb]">
              Comprar minutos
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
